import { useAuth } from "@/hooks/useAuth";
import { createContext, useContext, useState, type ReactNode } from "react";
import inovacenter_api from "@/services/inovacenter_api";
import { alert } from "@/contexts/AlertModalContext";
import { useGlobalLoader } from "@/components/GlobalLoader";
import { useImpressaoConfig } from "../ImpressaoConfigContext";

type OrdensDeImpressaoContextType = {
  coletarOrdens: () => Promise<void>;
  coletarOrdensImpressas: () => Promise<void>;
  definirOrdemComoImpressa: (ordem: string) => Promise<void>;
  definirOrdensComoImpressas: (ordens: string[]) => Promise<void>;
  obterOrdemBruta: (ordem: string) => Promise<unknown>;
  ordens: string[];
  ordensImpressas: string[];
  visualizarOrdem: (ordem: string) => Promise<string[][] | undefined>;
};

const OrdensDeImpressaoContext = createContext<
  OrdensDeImpressaoContextType | undefined
>(undefined);

export function OrdensDeImpressaoProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { impressaoConfig } = useImpressaoConfig();
  const { usuario } = useAuth();
  const { setIsLoading } = useGlobalLoader();
  const [ordens, setOrdens] = useState<string[]>([]);
  const [ordensImpressas, setOrdensImpressas] = useState<string[]>([]);

  const filtrarPelaConfig = (lista: string[]) => {
    const filtros = impressaoConfig?.filtro_ordem.split(",") || [];
    return lista.filter((ordem) =>
      filtros.some((filtro) => ordem.includes(filtro)),
    );
  };

  const obterOrdemBruta = async (ordem: string) => {
    const response = await inovacenter_api.get(`/impressao/filename/${ordem}`);
    return response.data;
  };

  const visualizarOrdem = async (ordem: string) => {
    try {
      const response_ordem = inovacenter_api.get<string[][]>(
        `/impressao/filename/${ordem}`,
      );
      return response_ordem.then((response) => {
        if (response.data.length === 0) {
          alert.error("Nenhum item encontrado para esta ordem.");
          return undefined;
        }
        return response.data;
      });
    } catch (error) {
      console.error("Erro ao coletar itens da ordem:", error);
      alert.error(
        "Erro ao coletar itens da ordem. Tente novamente mais tarde.",
      );
    }
  };

  const coletarOrdens = async () => {
    try {
      const response_ordens = await inovacenter_api.get<string[]>(
        `/impressao/${usuario?.nome}/${usuario?.id_conta}/*?list=true&filterName=nao-impresso`,
      );
      setOrdens(filtrarPelaConfig(response_ordens.data));
    } catch (error) {}
  };

  const coletarOrdensImpressas = async () => {
    setIsLoading(true);
    try {
      const response_ordens = await inovacenter_api.get<string[]>(
        `/impressao/${usuario?.nome}/${usuario?.id_conta}/*?list=true&filterName=impresso-`,
      );
      setOrdensImpressas(filtrarPelaConfig(response_ordens.data));
    } catch (error) {
      console.error("Erro ao coletar ordens impressas:", error);
      alert.error("Erro ao coletar ordens impressas. Tente novamente mais tarde.");
    } finally {
      setIsLoading(false);
    }
  };

  const definirOrdemComoImpressa = async (ordem: string) => {
    setIsLoading(true);
    try {
      await inovacenter_api.patch(
        `/impressao/updateName/${ordem}?printed=true`,
      );
      coletarOrdens();
    } catch (error) {
      console.error("Erro ao definir ordem como impressa:", error);
      alert.error("Erro ao apagar ordem. Tente novamente mais tarde.");
    } finally {
      setIsLoading(false);
    }
  };

  const definirOrdensComoImpressas = async (ordensParaApagar: string[]) => {
    if (ordensParaApagar.length === 0) return;
    setIsLoading(true);
    try {
      const resultados = await Promise.allSettled(
        ordensParaApagar.map((ordem) =>
          inovacenter_api.patch(`/impressao/updateName/${ordem}?printed=true`),
        ),
      );
      const falhas = resultados.filter((r) => r.status === "rejected").length;
      if (falhas > 0) {
        alert.error(
          `Não foi possível apagar ${falhas} de ${ordensParaApagar.length} ordens. Tente novamente mais tarde.`,
        );
      }
      await coletarOrdens();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <OrdensDeImpressaoContext.Provider
      value={{
        coletarOrdens,
        coletarOrdensImpressas,
        definirOrdemComoImpressa,
        definirOrdensComoImpressas,
        obterOrdemBruta,
        ordens,
        ordensImpressas,
        visualizarOrdem,
      }}
    >
      {children}
    </OrdensDeImpressaoContext.Provider>
  );
}

export function useOrdensDeImpressao() {
  const context = useContext(OrdensDeImpressaoContext);
  if (!context) {
    throw new Error(
      "useOrdensDeImpressao deve ser usado dentro de OrdensDeImpressaoProvider",
    );
  }
  return context;
}
